// The aroma icons: 17 family drawings (one per lexicon cluster) and 15 descriptor drawings, lit from
// the top left on a transparent ground so they sit on every theme. They are `<use>` references into
// one shared sprite that loads with the first icon, so a screen mounts nothing and the entry bundle
// carries none of the artwork. Icon ids: aromaIcons/ids.ts; word to icon: lib/aromaStyle.ts.
import { useEffect } from "react";
import { Svg } from "./Graphic";
import { aromaSymbolId, type AromaIconId } from "./aromaIcons/ids";

export { AROMA_ICON_IDS, type AromaIconId } from "./aromaIcons/ids";

let sprite: Promise<void> | undefined;

async function mountSprite(): Promise<void> {
  const { mountAromaSprite } = await import("./aromaIcons/Sprite");
  mountAromaSprite();
}

/** Loads the sprite chunk once; a failed load is forgotten, so the next icon to mount tries again. */
function loadSprite(): void {
  sprite ??= mountSprite().catch(() => {
    sprite = undefined;
  });
}

export interface AromaIconProps {
  id: AromaIconId;
  /** Rendered square in px; the drawings stay legible from 28 up to 56 and beyond. */
  size?: number;
  /** Announces the icon; without it the icon is decorative, because a visible label sits beside it. */
  label?: string | undefined;
}

export function AromaIcon({ id, size = 28, label }: AromaIconProps): React.JSX.Element {
  useEffect(loadSprite, []);
  return (
    <Svg viewBox="0 0 64 64" width={size} height={size} label={label}>
      <use href={`#${aromaSymbolId(id)}`} />
    </Svg>
  );
}
